import { useEffect, useMemo, useState, useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";

import {
  CategoriesModel,
  CategorySelectorModel,
  TransactionFormInterface,
  FileModel,
  AttachmentModel,
  PeriodModel,
  AssetModel,
  AssetType,
} from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  QUERY_WIDTHS,
  TRANSACTION_FILE_EXTENSIONS,
  transactionSchema,
  formatAmountAfterEditing,
  formatAmountOnBlur,
  getSubcategoryById,
  filterAssetsByDate,
} from "@utils/index";
import { attachmentsService } from "@services/index";
import {
  useTransactionGetOne,
  useTransactionGetCategories,
  useAssetGetBusinessAssets,
  useTransactionCreate,
  useTransactionUpdate,
  useTransactionFindPeriod,
} from "@providers/index";
import { useBusinessType, useNotify } from "@hooks/index";
import {
  MobileHeader,
  ChevronLeftSvg,
  Form,
  FormRow,
  CategorySelector,
  DatePicker,
  FormInput,
  FormText,
  FormFile,
  Button,
  SquareRoundedPlusSvg,
  DeviceFloppySvg,
  CheckupListSvg,
  XSvg,
  BusinessSelector,
  FileItem,
  Modal,
  ConfirmModal,
  AssetSelector,
} from "@components/index";
import { AssetForm } from "@pages/index";
import AlertImg from "@assets/images/alert-triangle.svg";

export const TransactionForm = ({
  isRefundForm,
}: {
  isRefundForm?: boolean;
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const idValue = location.pathname.split("/").slice(-1)[0];
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });

  const disabledFeatures = useUserStore(
    ({ disabledFeatures }) => disabledFeatures,
  );
  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");
  const businessType = useBusinessType(businessId);

  const [categories, setCategories] = useState<CategoriesModel>();
  const [category, setCategory] = useState<CategorySelectorModel>();
  const [excludedAssetTypes, setExcludedAssetTypes] = useState<AssetType[]>();

  const [date, setDate] = useState<Date | undefined>(new Date());
  const [period, setPeriod] = useState<PeriodModel>();

  const [assetId, setAssetId] = useState("");
  const [assets, setAssets] = useState<AssetModel[]>();
  const [previousDate, setPreviousDate] = useState<Date>();

  const [files, setFiles] = useState<FileModel[]>([]);
  const [attachments, setAttachments] = useState<AttachmentModel[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const [confirmBiggerAmount, setConfirmBiggerAmount] = useState(false);

  const [modalDuplicated, setModalDuplicated] = useState(false);
  const [modalAssetError, setModalAssetError] = useState(false);
  const [modalWrongAsetType, setModalWrongAsetType] = useState(false);
  const [modalHardMaxAmount, setModalHardMaxAmount] = useState(false);
  const [modalSoftMaxAmount, setModalSoftMaxAmount] = useState(false);
  const [confirmSoftMaxAmount, setConfirmSoftMaxAmount] = useState(false);
  const [modalSoftMinAmount, setModalSoftMinAmount] = useState(false);
  const [confirmSoftMinAmount, setConfirmSoftMinAmount] = useState(false);

  const subcategory = useMemo(
    () => getSubcategoryById(categories, category?.categoryId),
    [categories, category],
  );

  const dateStr = useMemo(() => {
    if (!date) {
      return "";
    }

    let dateStr = `${date.getFullYear()}`;
    dateStr += "-";
    dateStr += `0${date.getMonth() + 1}`.slice(-2);
    dateStr += "-";
    return (dateStr += `0${date.getDate()}`.slice(-2));
  }, [date]);

  const {
    isPending: isPendingGetOne,
    isSuccess: isSuccessGetOne,
    isStale: isStaleGetOne,
    data: dataGetOne,
    isError: isErrorGetOne,
    error: errorGetOne,
  } = useTransactionGetOne(
    ["refund", "create"].includes(idValue) ? "" : idValue,
  );

  useEffect(() => {
    if (isErrorGetOne && errorGetOne) {
      notify("error", errorGetOne.message);
    }
  }, [isErrorGetOne, errorGetOne, notify]);

  const {
    isPending: isPendingGetCategories,
    isSuccess: isSuccessGetCategories,
    data: dataGetCategories,
    isError: isErrorGetCategories,
    error: errorGetCategories,
  } = useTransactionGetCategories(businessType, true);

  useEffect(() => {
    if (isSuccessGetCategories && dataGetCategories) {
      setCategories(dataGetCategories);
    }
  }, [isSuccessGetCategories, dataGetCategories]);

  useEffect(() => {
    if (isErrorGetCategories && errorGetCategories) {
      notify("error", errorGetCategories.message);
    }
  }, [isErrorGetCategories, errorGetCategories, notify]);

  const {
    isPending: isPendingGetAssets,
    isSuccess: isSuccessGetAssets,
    data: dataGetAssets,
    isError: isErrorGetAssets,
    error: errorGetAssets,
  } = useAssetGetBusinessAssets(businessId);

  useEffect(() => {
    if (isSuccessGetAssets && dataGetAssets) {
      setAssets(dataGetAssets);
    }
  }, [isSuccessGetAssets, dataGetAssets]);

  useEffect(() => {
    if (isErrorGetAssets && errorGetAssets) {
      notify("error", errorGetAssets.message);
    }
  }, [isErrorGetAssets, errorGetAssets, notify]);

  const {
    isPending: isPendingFindPeriod,
    isSuccess: isSuccessFindPeriod,
    data: dataFindPeriod,
    isError: isErrorFindPeriod,
    error: errorFindPeriod,
  } = useTransactionFindPeriod({ date: dateStr });

  useEffect(() => {
    if (isSuccessFindPeriod && dataFindPeriod) {
      setPeriod(dataFindPeriod);
      setPreviousDate(undefined);
    }
  }, [isSuccessFindPeriod, dataFindPeriod]);

  const {
    mutate: mutateCreateTransaction,
    isPending: isPendingCreateTransaction,
  } = useTransactionCreate(
    (pressedCreateNewBtn) => {
      notify("success", "Transaction created successfully.");
      if (pressedCreateNewBtn) {
        const businessId = defaultBusiness?.uniqueId || businesses[0]?.uniqueId;
        setBusinessId(businessId);
        setCategory(undefined);
        setAssetId("");
        const date = new Date();
        setDate(date);
        reset({
          businessId,
          categoryId: "",
          assetId: "",
          date,
          isDescriptionRequired: false,
          isAssetRequired: false,
          amount: "",
          description: "",
        });
        setFiles([]);
      }
      navigate(`/transactions${pressedCreateNewBtn ? "/create" : ""}`);
    },
    ({ message, fields }) => {
      if (
        message ===
        "Duplicate Transaction Detected. Please add a unique description or file"
      ) {
        setModalDuplicated(true);
        return;
      } else if (
        message.includes(
          "You cannot create a Leased / HP / Loan transaction because the selected asset is not marked as Leased, Hire Purchase, or",
        )
      ) {
        setModalWrongAsetType(true);
        return;
      }
      Object.keys(fields || {}).forEach((key) =>
        setError(
          key as
            | "businessId"
            | "categoryId"
            | "date"
            | "amount"
            | "description",
          { message: fields![key] },
        ),
      );
      notify("error", message);
    },
  );

  const {
    mutate: mutateUpdateTransaction,
    isPending: isPendingUpdateTransaction,
  } = useTransactionUpdate(
    (pressedCreateNewBtn) => {
      notify("success", "Transaction updated successfully.");
      navigate(`/transactions${pressedCreateNewBtn ? "/create" : ""}`);
    },
    ({ message, fields }) => {
      if (
        message ===
        "Duplicate Transaction Detected. Please add a unique description or file"
      ) {
        setModalDuplicated(true);
        return;
      } else if (
        message.includes(
          "You cannot create a Leased / HP / Loan transaction because the selected asset is not marked as Leased, Hire Purchase, or",
        )
      ) {
        setModalWrongAsetType(true);
        return;
      }
      Object.keys(fields || {}).forEach((key) =>
        setError(
          key as
            | "businessId"
            | "categoryId"
            | "date"
            | "amount"
            | "description",
          { message: fields![key] },
        ),
      );
      notify("error", message);
    },
  );

  useEffect(() => {
    setState({
      isLoading:
        (isStaleGetOne && isPendingGetOne) ||
        isPendingGetCategories ||
        isPendingGetAssets ||
        isPendingCreateTransaction ||
        isPendingUpdateTransaction ||
        isPendingFindPeriod,
    });
  }, [
    isStaleGetOne,
    isPendingGetOne,
    isPendingGetCategories,
    isPendingGetAssets,
    isPendingCreateTransaction,
    isPendingUpdateTransaction,
    isPendingFindPeriod,
    setState,
  ]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
    getValues,
  } = useForm<TransactionFormInterface>({
    mode: "onBlur",
    disabled:
      (isStaleGetOne && isPendingGetOne) ||
      isPendingGetCategories ||
      isPendingGetAssets ||
      isPendingCreateTransaction ||
      isPendingUpdateTransaction ||
      isPendingFindPeriod,
    resolver: yupResolver(transactionSchema),
  });

  const pickAsset = useCallback(
    ({
      categoryIdValue,
      assetId,
      assets = [],
      categories,
      category,
      date,
      period,
    }: {
      categoryIdValue?: string;
      assetId?: string;
      assets?: AssetModel[];
      categories?: CategoriesModel;
      category?: CategorySelectorModel;
      date?: Date;
      period?: PeriodModel;
    }) => {
      const subcat = getSubcategoryById(
        categories,
        categoryIdValue || category?.categoryId,
      );
      const periodStartDateRez = period
        ? new Date(period.startDate)
        : undefined;
      const filteredAssets = filterAssetsByDate({
        assets,
        date,
        startPeriodDate: periodStartDateRez,
        excludeTypes: subcat?.hideFromAssetTypes,
        hideAssetsWithMiles: businessType === "other",
      });
      const defaultAsset = filteredAssets.find(({ isDefault }) => isDefault);
      if (
        assetId &&
        !filteredAssets.find(({ uniqueId }) => uniqueId === assetId)
      ) {
        setAssetId("");
        setValue("assetId", "");
        clearErrors("assetId");
      } else if (!assetId && defaultAsset) {
        setAssetId(defaultAsset.uniqueId);
        setValue("assetId", defaultAsset.uniqueId);
        clearErrors("assetId");
      } else if (
        !assetId &&
        filteredAssets.filter(({ disposal }) => !disposal).length === 1
      ) {
        setAssetId(
          filteredAssets.filter(({ disposal }) => !disposal)[0].uniqueId,
        );
        setValue(
          "assetId",
          filteredAssets.filter(({ disposal }) => !disposal)[0].uniqueId,
        );
        clearErrors("assetId");
      }
    },
    [clearErrors, setValue, businessType],
  );

  useEffect(() => {
    if (category && period?.startDate && assets && !previousDate) {
      setPreviousDate(date);
      const subcat = getSubcategoryById(categories, category.categoryId);
      if (!disabledFeatures.includes("Assets") && subcat?.assetRequired) {
        pickAsset({ assetId, assets, categories, category, date, period });
      }
    }
  }, [
    categories,
    category,
    period,
    pickAsset,
    previousDate,
    date,
    assetId,
    assets,
    disabledFeatures,
  ]);

  useEffect(() => {
    if (isSuccessGetOne && dataGetOne) {
      setBusinessId(dataGetOne.userBusiness.uniqueId);
      setCategory({
        type: dataGetOne.type,
        categoryId: dataGetOne.subcategory.uniqueId,
      });
      setExcludedAssetTypes(dataGetOne.subcategory.hideFromAssetTypes);
      const assetId = dataGetOne.asset?.uniqueId || "";
      setAssetId(assetId);
      const date = !isRefundForm ? new Date(dataGetOne.date) : new Date();
      setDate(date);
      reset({
        businessId: dataGetOne.userBusiness.uniqueId,
        categoryId: dataGetOne.subcategory.uniqueId,
        isDescriptionRequired:
          dataGetOne.subcategory.transactionDescriptionRequired,
        isAssetRequired: disabledFeatures.includes("Assets")
          ? false
          : dataGetOne.subcategory.assetRequired,
        assetId,
        date,
        amount: formatAmountAfterEditing(dataGetOne.amount),
        description: !isRefundForm
          ? dataGetOne.description || ""
          : "Refund for a Transaction",
      });
      if (!isRefundForm) {
        setAttachments(dataGetOne.attachments || []);
      }
    } else {
      const businessId = defaultBusiness?.uniqueId || businesses[0]?.uniqueId;
      setBusinessId(businessId);
      setCategory(undefined);
      setAssetId("");
      const date = new Date();
      setDate(date);
      reset({
        businessId,
        categoryId: "",
        assetId: "",
        date,
        isDescriptionRequired: false,
        isAssetRequired: false,
        amount: "",
        description: "",
      });
      setFiles([]);
    }
  }, [
    isSuccessGetOne,
    dataGetOne,
    disabledFeatures,
    isRefundForm,
    defaultBusiness,
    businesses,
    reset,
  ]);

  useEffect(() => {
    if (isErrorFindPeriod && errorFindPeriod) {
      setPeriod(undefined);
      setError("date", { message: errorFindPeriod.message });
    }
  }, [isErrorFindPeriod, errorFindPeriod, setError]);

  useEffect(() => {
    if (!modalAssetError && formState.errors.assetId?.message) {
      setModalAssetError(true);
    }
  }, [modalAssetError, formState]);

  const handleSelectBusiness = (businessId: string) => {
    setBusinessId(businessId);
    setValue("businessId", businessId);
    setCategory(undefined);
    setValue("categoryId", "");
    clearErrors("description");
    setAssetId("");
    setValue("assetId", "");
  };

  const handleSelectCategory = (value?: CategorySelectorModel) => {
    setCategory(value);
    setValue("categoryId", value?.categoryId || "");
    clearErrors("categoryId");
    const subcat = getSubcategoryById(categories, value?.categoryId);
    setValue("isDescriptionRequired", subcat?.transactionDescriptionRequired);
    clearErrors("isDescriptionRequired");
    clearErrors("isAssetRequired");
    clearErrors("description");
    if (!disabledFeatures.includes("Assets")) {
      setValue("isAssetRequired", subcat?.assetRequired);
      setExcludedAssetTypes(subcat?.hideFromAssetTypes);
      setAssetId("");
      setValue("assetId", "");
      if (subcat?.assetRequired) {
        pickAsset({
          categoryIdValue: value?.categoryId,
          assetId,
          assets,
          categories,
          category,
          date,
          period,
        });
      }
    }
  };

  const handleSelectAsset = (assetId: string) => {
    setAssetId(assetId);
    setValue("assetId", assetId);
    clearErrors("assetId");
  };

  const handleSelectDate = (value?: Date) => {
    setDate(value || new Date());
    setValue("date", value || new Date());
    clearErrors("date");
  };

  const handleSelectFiles = async (target: HTMLInputElement) => {
    setIsUploading(true);
    if (!target.files?.length) {
      setIsUploading(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      setIsUploading(false);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index]?.size || 0,
    }));
    setFiles([...files, ...newFilesWithSizes]);
    clearErrors("files");
    setIsUploading(false);
  };

  const removeFile = (uuid: string) => {
    const newFiles = files.filter((file) => file.uuid !== uuid);
    setFiles(newFiles);
  };

  const removeAttachment = (uniqueId: string) => {
    const newAttachments = attachments.filter(
      (attachment) => attachment.uniqueId !== uniqueId,
    );
    setAttachments(newAttachments);
  };

  const onSubmit: SubmitHandler<TransactionFormInterface> = (data) => {
    const amount = data.amount.replaceAll(/[^0-9/.]*/g, "") || "0";
    if (
      !!subcategory?.hardMaxAmount &&
      parseFloat(amount) >= parseInt(subcategory.hardMaxAmount)
    ) {
      setModalHardMaxAmount(true);
      return;
    }
    if (
      !!subcategory?.softMaxAmount &&
      !confirmSoftMaxAmount &&
      parseFloat(amount) >= parseInt(subcategory.softMaxAmount)
    ) {
      setModalSoftMaxAmount(true);
      return;
    }
    if (
      !!subcategory?.softMinAmount &&
      !confirmSoftMinAmount &&
      parseFloat(amount) <= parseInt(subcategory.softMinAmount)
    ) {
      setModalSoftMinAmount(true);
      return;
    }
    setModalSoftMaxAmount(false);
    setModalSoftMinAmount(false);

    const transactionAmount = dataGetOne?.amount
      ? parseFloat(dataGetOne?.amount)
      : 0;
    if (
      isRefundForm &&
      idValue !== "refund" &&
      parseFloat(amount) > transactionAmount
    ) {
      setConfirmBiggerAmount(true);
      return;
    }

    return idValue && !["refund", "create"].includes(idValue) && !isRefundForm
      ? mutateUpdateTransaction({
          ...data,
          transactionId: idValue,
          attachments,
          files,
          amount,
        })
      : mutateCreateTransaction({
          ...data,
          refund: isRefundForm,
          refundOf: isRefundForm && idValue !== "refund" ? idValue : undefined,
          files,
          amount,
        });
  };

  const confirmSubmit = () => {
    setConfirmBiggerAmount(false);
    const data = getValues();
    mutateCreateTransaction({
      ...data,
      date: new Date(data.date),
      refund: true,
      refundOf: idValue,
      files,
      amount: data.amount.replaceAll(/[^0-9/.]*/g, "") || "0",
    });
  };

  const handleCloseModalAssetError = () => {
    setModalAssetError(false);
    clearErrors("assetId");
  };

  return (
    <div className="min-h-dvh bg-white-100 md:min-h-0 md:bg-lightGray">
      {isMobile && (
        <MobileHeader
          href="/transactions"
          title={
            (isRefundForm && "Transaction Refund") ||
            (dataGetOne && isSuccessGetOne && "Edit Transaction") ||
            "Add Transaction"
          }
        />
      )}
      {!isMobile && (
        <div className="mt-4 flex h-16 items-center gap-x-2 bg-lightGray pr-4 md:mt-0 md:h-24 md:pr-8">
          <Link
            to="/transactions"
            className="flex h-8 w-8 items-center justify-center rounded-e-lg border border-black-8 bg-white-100 text-primary-300"
          >
            <ChevronLeftSvg />
          </Link>
          <h1 className="text-400 font-bold text-black-100 md:text-600">
            {(isRefundForm && "Transaction Refund") ||
              (dataGetOne && isSuccessGetOne && "Edit Transaction") ||
              "Add Transaction"}
          </h1>
        </div>
      )}
      <Form
        onSubmit={handleSubmit(onSubmit)}
        className="px-4 text-black-100 md:min-h-[calc(100dvh_-_248px)] md:rounded-b-lg md:rounded-t-3xl md:px-10"
      >
        <div className="flex flex-col md:flex-row md:gap-x-10">
          <div className="md:w-[432px]">
            <input
              {...register<"pressedCreateNewBtn">("pressedCreateNewBtn")}
              type="hidden"
              name="pressedCreateNewBtn"
            />
            <FormRow
              top={isMobile ? "large" : "medium"}
              className="text-300 font-bold md:text-400"
            >
              Transaction information
            </FormRow>
            <FormRow top={isMobile ? "small" : "large"}>
              <BusinessSelector
                value={businessId}
                onSetBusiness={handleSelectBusiness}
                variant="formField"
                dataTestid="input_businessId"
              />
              <input
                {...register<"businessId">("businessId")}
                type="hidden"
                name="businessId"
              />
            </FormRow>
            <FormRow top="small">
              <CategorySelector
                categories={categories}
                value={category}
                dataTestid="input_categoryId"
                onSetCategory={handleSelectCategory}
                error={formState.errors.categoryId?.message}
              />
              <input
                {...register<"categoryId">("categoryId")}
                type="hidden"
                name="categoryId"
              />
            </FormRow>
            {!disabledFeatures.includes("Assets") && (
              <FormRow
                top="small"
                className={clsx(
                  !getSubcategoryById(categories, category?.categoryId)
                    ?.assetRequired && "hidden",
                )}
              >
                <AssetSelector
                  businessType={businessType}
                  items={assets || []}
                  date={date}
                  startPeriodDate={
                    period ? new Date(period.startDate) : undefined
                  }
                  excludeTypes={excludedAssetTypes}
                  hideAssetsWithMiles
                  value={assetId}
                  error={formState.errors.assetId?.message}
                  onSetAsset={handleSelectAsset}
                />
                <input
                  {...register<"assetId">("assetId")}
                  type="hidden"
                  name="assetId"
                />
                <input
                  {...register<"isAssetRequired">("isAssetRequired")}
                  type="hidden"
                  name="isAssetRequired"
                />
              </FormRow>
            )}
            <FormRow top="small">
              <DatePicker
                date={date}
                prefix="transactionForm"
                dataTestid="input_date"
                onSetDate={handleSelectDate}
                error={formState.errors.date?.message}
              />
              <input {...register<"date">("date")} type="hidden" name="date" />
              {period && (
                <div className="border-l-4 border-l-primary-300 bg-primary-104 px-4 py-2">
                  <div className="text-100 text-black-54">Period</div>
                  <div className="pt-2 text-black-100">{period.title}</div>
                </div>
              )}
            </FormRow>
          </div>
          <div className="md:w-[432px]">
            <FormRow
              top={isMobile ? "large" : "medium"}
              className="text-300 font-bold md:text-400"
            >
              Transaction details
            </FormRow>
            <FormRow top={isMobile ? "small" : "large"}>
              <FormInput
                {...register<"amount">("amount")}
                name="amount"
                label="Amount"
                type="text"
                maxLength={13}
                data-testid="input_amount"
                error={formState.errors.amount?.message}
                onChange={({ currentTarget }) => {
                  setValue("amount", currentTarget.value);
                  clearErrors("amount");
                }}
                onBlur={({ currentTarget }) =>
                  setValue("amount", formatAmountOnBlur(currentTarget.value))
                }
              />
            </FormRow>
            <FormRow top="small">
              <FormText
                {...register<"description">("description")}
                name="description"
                label="Description"
                data-testid="input_description"
                error={formState.errors.description?.message}
                onChange={() => clearErrors("description")}
                className="min-h-[136px]"
              />
              <input
                {...register<"isDescriptionRequired">("isDescriptionRequired")}
                type="hidden"
                name="isDescriptionRequired"
              />
            </FormRow>
          </div>
        </div>
        <FormRow top="large" className="text-300 font-bold md:text-400">
          Transaction Receipts
        </FormRow>
        <FormRow top={isMobile ? "small" : "large"}>
          <FormFile
            {...register<"files">("files")}
            name="files"
            dataTestid="input_files"
            error={formState.errors.files?.message}
            accept={TRANSACTION_FILE_EXTENSIONS}
            uploading={isUploading}
            onChange={(event) => {
              handleSelectFiles(event.target as HTMLInputElement);
              event.preventDefault();
            }}
            className="max-w-[430px]"
          />
        </FormRow>
        {isRefundForm || !attachments.length ? null : (
          <div className="mt-4 flex max-w-[430px] flex-col gap-y-4">
            {attachments.map((attachment) => (
              <FileItem
                key={attachment.uniqueId}
                file={attachment}
                onRemove={removeAttachment}
              />
            ))}
          </div>
        )}
        {!files.length ? null : (
          <div className="mt-4 flex max-w-[430px] flex-col gap-y-4">
            {files.map((file) => (
              <FileItem key={file.uuid} file={file} onRemove={removeFile} />
            ))}
          </div>
        )}
        {!isMobile && (
          <div className="-mx-10 mt-10 h-px w-[calc(100%_+_80px)] bg-black-8" />
        )}
        <FormRow
          isRow={!isMobile}
          isBetween
          top="small"
          className="gap-y-4 pb-6"
        >
          <div
            className="flex flex-col gap-4 md:flex-row"
            data-testid="buttons_saveTransaction"
          >
            <Button
              type="submit"
              title={
                (isRefundForm && "Save Refund") ||
                (dataGetOne && isSuccessGetOne && "Update") ||
                "Save Transaction"
              }
              leftIcon={
                dataGetOne && isSuccessGetOne ? (
                  <DeviceFloppySvg />
                ) : (
                  <SquareRoundedPlusSvg />
                )
              }
              className="w-full md:w-fit md:px-5"
              height="small"
              dataTestid="button_saveTransaction"
              disabled={
                !isRefundForm &&
                dataGetOne &&
                isSuccessGetOne &&
                ["submitted", "completed"].includes(dataGetOne.status.value)
              }
            />
            {!isRefundForm && (
              <Button
                type="submit"
                title="Save and Add Another"
                leftIcon={<CheckupListSvg />}
                height="small"
                variant="secondary"
                onClick={() => setValue("pressedCreateNewBtn", true)}
              />
            )}
          </div>
          <Button
            title="Cancel"
            variant="secondary"
            height="small"
            leftIcon={<XSvg />}
            isLink
            href="/transactions"
          />
        </FormRow>
        <ConfirmModal
          show={modalSoftMaxAmount}
          confirmButtonTitle="Save"
          confirmButtonType="submit"
          cancelButtonTitle="Edit"
          text2={
            subcategory?.softMaxAmountMessage ||
            "This amount appears higher than we'd expect to see, is it correct?"
          }
          onClickCancel={() => setModalSoftMaxAmount(false)}
          onClickConfirm={() => setConfirmSoftMaxAmount(true)}
        />
        <ConfirmModal
          show={modalSoftMinAmount}
          confirmButtonTitle="Save"
          confirmButtonType="submit"
          cancelButtonTitle="Edit"
          text2={
            subcategory?.softMinAmountMessage ||
            "This amount appears lower than we'd expect to see, is it correct?"
          }
          onClickCancel={() => setModalSoftMinAmount(false)}
          onClickConfirm={() => setConfirmSoftMinAmount(true)}
        />
      </Form>
      {isRefundForm && (
        <ConfirmModal
          show={confirmBiggerAmount}
          confirmButtonTitle="Yes"
          text1="Are you sure?"
          text2="The Refund Amount you've entered exceeds the Transaction Amount. Do you want to proceed?"
          onClickCancel={() => setConfirmBiggerAmount(false)}
          onClickConfirm={confirmSubmit}
        />
      )}
      {assetId === "new" && (
        <AssetForm
          initialBusinessId={businessId}
          excludeTypes={excludedAssetTypes}
          onClose={() => {
            setAssetId("");
            setValue("assetId", "");
            clearErrors("assetId");
          }}
          onCreated={handleSelectAsset}
        />
      )}
      <Modal show={modalDuplicated} onClose={() => setModalDuplicated(false)}>
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-2 text-400 font-bold text-black-100 md:pt-4 md:text-500">
            Duplicate Transaction Detected
          </div>
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            It looks like you're trying to save a transaction with the same
            type, category, subcategory and amount as an existing one. To save
            this as a separate transaction, please add a unique description.
            This helps avoid confusion and ensures your records stay accurate.
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="Back to Editing"
            className="mx-auto w-40"
            onClick={() => setModalDuplicated(false)}
          />
        </div>
      </Modal>
      <Modal
        show={modalAssetError}
        className="max-w-[520px]"
        onClose={handleCloseModalAssetError}
      >
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-2 text-400 font-bold text-black-100 md:pt-4 md:text-500">
            Asset field is required
          </div>
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            Please select an existing asset or create a new one.
          </div>
        </div>
        <div className="flex flex-col gap-y-4 pt-8 md:pt-12">
          <Button
            isLink
            title="Click here for detailed instructions"
            href="https://vimeo.com/1100639491"
            target="_blank"
            className="w-full"
          />
          <Button
            type="button"
            variant="secondary"
            title="Cancel"
            className="w-full"
            onClick={handleCloseModalAssetError}
          />
        </div>
      </Modal>
      <Modal
        show={modalWrongAsetType}
        onClose={() => setModalWrongAsetType(false)}
      >
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-2 text-400 font-bold text-black-100 md:pt-4 md:text-500">
            Transaction Not Allowed
          </div>
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            You cannot create a <strong>Leased / HP / Loan transaction</strong>{" "}
            because the selected asset is not marked as{" "}
            <strong>Leased, Hire Purchase,</strong> or <strong>Loan</strong>.
            <br />
            Please update the asset details or select a valid asset before
            continuing.
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="Ok"
            className="mx-auto w-40"
            onClick={() => setModalWrongAsetType(false)}
          />
        </div>
      </Modal>
      <Modal
        show={modalHardMaxAmount}
        onClose={() => setModalHardMaxAmount(false)}
      >
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            {subcategory?.hardMaxAmountMessage || (
              <div>
                This exceeds the maximum value for this transaction type.
                <br />
                Please review the amount, if you believe it is correct please
                contact us.
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="Ok"
            className="mx-auto w-40"
            onClick={() => setModalHardMaxAmount(false)}
          />
        </div>
      </Modal>
    </div>
  );
};
